import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  Figure,
  LoadMore,
  Select,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TH,
  THEAD,
  caseHref,
  useMoney,
} from '@/pages/cvm/shared';
import { fetchCvmConsistency } from '@/services/cvm';
import type { CvmConsistencyPair } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Comparable judgments: pairs of final judgments on a shared violated provision.
 *
 * A pair is two judgments whose sanction rows cite the same normalised
 * provision and share at least one sanction type; each side prints its fines
 * so a reader compares them with the clause in view. This is a queue for a
 * lawyer to read, never a verdict: the contradiction review (an LLM reading
 * both Votos) is refused with its reason, and no pair is scored.
 */
export function CvmConsistencyPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [provision, setProvision] = useState(seed.get('provision') ?? '');
  const filters = { provision: provision || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'consistency', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmConsistency({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.pairs.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const pairs = paged?.pages.flatMap((page) => page.pairs) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:consistency.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4" data-queue>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:consistency.queue.title')}
            </h2>
            <Answer answer={data.queue} help={t('cvm:consistency.queue.help')}>
              {(qshape) => (
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Figure
                    label={t('cvm:consistency.queue.pairs')}
                    value={number.format(qshape.pairs)}
                  />
                  <Figure
                    label={t('cvm:consistency.queue.decisions')}
                    value={number.format(qshape.decisions_in_pairs)}
                    hint={t('cvm:consistency.queue.ofFinal', {
                      n: number.format(qshape.final_decisions),
                    })}
                  />
                  <Figure
                    label={t('cvm:consistency.queue.provisions')}
                    value={number.format(qshape.provisions_in_pairs)}
                  />
                  <Figure
                    label={t('cvm:consistency.queue.citations')}
                    value={number.format(qshape.citations)}
                    hint={t('cvm:consistency.queue.citationsHint', {
                      resolved: number.format(qshape.citations_resolved),
                      citing: number.format(qshape.citing_decisions),
                    })}
                  />
                </dl>
              )}
            </Answer>
          </section>
          <RefusalCard
            title={t('cvm:consistency.contradictions.title')}
            answer={data.contradictions}
          />
        </div>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          id="cvm-consistency-provision"
          label={t('cvm:consistency.filters.provision')}
          any={t('cvm:consistency.filters.any')}
          value={provision}
          onChange={setProvision}
          options={data?.provisions}
        />
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(pairs.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:consistency.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:consistency.columns.provision')}</th>
                  <th className={TH}>{t('cvm:consistency.columns.a')}</th>
                  <th className={TH}>{t('cvm:consistency.columns.b')}</th>
                  <th className={TH}>{t('cvm:consistency.columns.shared')}</th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair, index) => (
                  <PairRow
                    key={`${pair.case_key_a}-${pair.case_key_b}-${pair.provision}-${index}`}
                    pair={pair}
                  />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      <FieldGuide i18nKey="cvm:consistency.guide" />
    </div>
  );
}

function Side({
  caseKey,
  process,
  date,
  relator,
  fines,
}: {
  caseKey: string;
  process: string;
  date: string | null;
  relator: string | null;
  fines: string[];
}) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  return (
    <div>
      <Link to={caseHref(caseKey)} className="text-primary underline-offset-2 hover:underline">
        {process}
      </Link>
      <div className="text-xs text-muted-foreground">
        {date ?? '—'}
        {relator ? ` · ${relator}` : ''}
      </div>
      <div className="text-xs tabular-nums text-foreground">
        {fines.length ? fines.map((f) => money(f)).join(' · ') : t('cvm:consistency.noFine')}
      </div>
    </div>
  );
}

function PairRow({ pair }: { pair: CvmConsistencyPair }) {
  const { t } = useTranslation(['cvm']);
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD}>{pair.provision}</td>
      <td className={TD}>
        <Side
          caseKey={pair.case_key_a}
          process={pair.process_number_a}
          date={pair.judgment_date_a}
          relator={pair.relator_a}
          fines={pair.fines_a}
        />
      </td>
      <td className={TD}>
        <Side
          caseKey={pair.case_key_b}
          process={pair.process_number_b}
          date={pair.judgment_date_b}
          relator={pair.relator_b}
          fines={pair.fines_b}
        />
      </td>
      <td className={TD_MUTED}>
        {pair.shared_sanction_types
          .map((code) => t(`cvm:sanctionTypes.${code}`, { defaultValue: code }))
          .join(' · ')}
      </td>
    </tr>
  );
}
